import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

const TIMEFRAME_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 14, label: 'Last 14 days' },
  { value: 28, label: 'Last 28 days' },
  { value: 60, label: 'Last 60 days' },
  { value: 90, label: 'Last 90 days' },
];

const SyncLineChart = ({ data, lines, height = 400 }) => (
  <Box sx={{ width: '100%', height }}>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="dateLabel" />
        <YAxis />
        <Tooltip />
        <Legend />
        {lines.map((line) => (
          <Line
            key={line.dataKey}
            type="monotone"
            dataKey={line.dataKey}
            name={line.name}
            stroke={line.color}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  </Box>
);

const AdminDailyStatisticsView = ({ companies = [] }) => {
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState([]);
  const [days, setDays] = useState(28);
  const [companyId, setCompanyId] = useState('');

  useEffect(() => {
    if (!apiCall) return;

    let cancelled = false;

    const loadChartData = async () => {
      setLoading(true);
      setError('');

      try {
        let url = `/api/admin/daily-statistics?days=${days}`;
        if (companyId) {
          url += `&company_id=${companyId}`;
        }

        const result = await apiCall('get', url);

        if (cancelled) return;

        if (!result.success) {
          setError(result.error || 'Failed to load statistics');
          setChartData([]);
        } else {
          const rows = (result.data?.data || []).map((row) => ({
            ...row,
            dateLabel: dayjs(row.snapshot_date).format('MMM D'),
          }));
          setChartData(rows);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load statistics');
          setChartData([]);
        }
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    loadChartData();

    return () => {
      cancelled = true;
    };
  }, [days, companyId, apiCall]);

  const loginsLines = [
    {
      dataKey: 'logins',
      name: 'Users logins',
      color: '#1565c0',
    },
  ];

  const emailsLines = [
    {
      dataKey: 'emails_sent',
      name: 'Emails sent',
      color: '#2e7d32',
    },
  ];

  const salesEmailsLines = [
    {
      dataKey: 'sales_emails_sent',
      name: 'Sales emails sent',
      color: '#ed6c02',
    },
  ];

  const showSalesChart = !companyId;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Company</InputLabel>
          <Select
            value={companyId}
            label="Company"
            onChange={(e) => setCompanyId(e.target.value)}
          >
            <MenuItem value="">All companies</MenuItem>
            {companies.map((company) => (
              <MenuItem key={company.id} value={String(company.id)}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Timeframe</InputLabel>
          <Select
            value={days}
            label="Timeframe"
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {TIMEFRAME_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : chartData.length === 0 && !error ? (
        <Alert severity="info">
          No statistics for this period. Daily snapshots are created by the scheduled admin statistics job.
        </Alert>
      ) : (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Users logins
          </Typography>
          <SyncLineChart data={chartData} lines={loginsLines} />

          <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
            Emails sent
          </Typography>
          <SyncLineChart data={chartData} lines={emailsLines} />

          {showSalesChart && (
            <>
              <Typography variant="h6" sx={{ mb: 2, mt: 4 }}>
                Sales emails sent
              </Typography>
              <SyncLineChart data={chartData} lines={salesEmailsLines} />
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AdminDailyStatisticsView;
